import { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';

interface ModalReprovacaoProps {
  show: boolean;
  relatorio: RelatorioAtividadeDto | null;
  onHide: () => void;
  /** No contexto corregedoria, destino é passado como segundo argumento. */
  onConfirm: (justificativa: string, destino?: 'diretor' | 'defensor') => void;
  loading?: boolean;
  /** Contexto de uso do modal: ajusta textos e opções de destino. */
  contexto?: 'diretoria' | 'corregedoria';
}

export default function ModalReprovacao({
  show,
  relatorio,
  onHide,
  onConfirm,
  loading = false,
  contexto = 'diretoria',
}: ModalReprovacaoProps) {
  const [justificativa, setJustificativa] = useState('');
  const [error, setError] = useState('');
  const [destino, setDestino] = useState<'diretor' | 'defensor'>('defensor');

  const handleConfirm = () => {
    if (!justificativa.trim()) {
      setError('A justificativa é obrigatória');
      return;
    }

    if (justificativa.trim().length < 10) {
      setError('A justificativa deve ter pelo menos 10 caracteres');
      return;
    }

    setError('');

    let texto = justificativa.trim();
    if (contexto === 'corregedoria') {
      const prefixo =
        destino === 'diretor'
          ? '[Destinatário: Diretor] '
          : '[Destinatário: Defensor] ';
      texto = `${prefixo}${texto}`;
    }

    onConfirm(texto, contexto === 'corregedoria' ? destino : undefined);
    setJustificativa(''); // Limpar após confirmar
  };

  const handleHide = () => {
    setJustificativa('');
    setDestino('defensor');
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Reprovar Relatório</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {relatorio && (
          <>
            <div className="mb-3">
              <p className="mb-1">
                <strong>Defensor:</strong> {relatorio.defensorNome || 'Não informado'}
              </p>
              <p className="mb-1">
                <strong>Defensoria/Vara:</strong> {relatorio.defensoriaName}
              </p>
              <p className="mb-1">
                <strong>Período:</strong> {relatorio.periodo}
              </p>
              <p className="mb-3">
                <strong>Tipo:</strong> {relatorio.tipo === 'civil' ? 'Cível' : 'Criminal'}
              </p>
            </div>

            {contexto === 'corregedoria' ? (
              <div className="alert alert-warning" role="alert">
                <strong>Atenção!</strong> Escolha para onde a reprovação será encaminhada:
                <ul className="mb-0 mt-2">
                  <li><strong>Diretor</strong>: o relatório retorna ao diretor regional, que poderá solicitar ajustes ao defensor.</li>
                  <li><strong>Defensor</strong>: o relatório retorna diretamente ao defensor para correções.</li>
                </ul>
              </div>
            ) : (
              <div className="alert alert-warning" role="alert">
                <strong>Atenção!</strong> Ao reprovar este relatório, o defensor será notificado
                e poderá fazer as correções necessárias.
              </div>
            )}

            {contexto === 'corregedoria' && (
              <Form.Group className="mb-3">
                <Form.Label>Destino da reprovação</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  <Form.Check
                    type="radio"
                    id="destino-diretor"
                    name="destino-reprovacao"
                    label="Enviar de volta para o diretor"
                    checked={destino === 'diretor'}
                    onChange={() => setDestino('diretor')}
                  />
                  <Form.Check
                    type="radio"
                    id="destino-defensor"
                    name="destino-reprovacao"
                    label="Enviar diretamente para o defensor"
                    checked={destino === 'defensor'}
                    onChange={() => setDestino('defensor')}
                  />
                </div>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Justificativa da Reprovação <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={justificativa}
                onChange={(e) => {
                  setJustificativa(e.target.value);
                  setError('');
                }}
                placeholder="Descreva o motivo da reprovação do relatório..."
                isInvalid={!!error}
              />
              <Form.Text className="text-muted">
                Mínimo de 10 caracteres. Seja claro e objetivo.
              </Form.Text>
              {error && <div className="invalid-feedback d-block">{error}</div>}
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={loading || !justificativa.trim()}>
          {loading ? 'Reprovando...' : 'Confirmar Reprovação'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
